/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

/**
 * SelfCheckIn — the participant's own check-in, opened from a pass card.
 *
 * One section in the page's own card, listing the seats this visitor holds for
 * THIS event: who is using each one, where it stands, and the one button its
 * pass status allows. Checking in asks for the person's details first, because
 * a seat booked for somebody else knows nothing about them and a certificate
 * has to carry a name.
 *
 * PRESENTATIONAL ON PURPOSE. It is handed the passes and it hands back what
 * somebody pressed; the reading and the writing live on the page, where the
 * action plumbing and the session already are. That is what makes every state
 * below testable without a server: pass the rows, assert the screen.
 *
 * EVERY SEAT IS CHECKED IN SEPARATELY, however many somebody holds. A booker
 * with four places has four people arriving, each with their own name and
 * their own certificate, so each seat carries its own button and its own
 * details. The request names which pass, and the server verifies that pass
 * belongs to the person who sent it before it writes anything (the action
 * endpoint's `owner_checked` rule) — naming a pass is only safe BECAUSE of
 * that check.
 */

import React, { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  selfCheckInView, detailsCheck, detailsMissing, mergedUserInfo,
  FIELD_LIMITS, SEAT_ACTION,
} from './selfCheckInRules';
// The dialog opens on My Registrations too, which loads no event-page styles.
// Everything in it is scoped to .evb-*, so it adds nothing to that page.
import '../../../styles/eventDetail.css';

/* The stored action buttons this section runs. They are configuration, not
 * code: the server reads what each one writes from the event view's own
 * action_buttons, and the browser only ever sends the id. */
export const CHECK_IN_BUTTON = 'self_check_in';
export const CHECK_OUT_BUTTON = 'self_check_out';

/* `required` is the certificate's requirement, not a form convention: the name
 * is printed on it and the address is where it is sent. The other two are kept
 * when given and never insisted on. */
const FIELDS = [
  { key: 'first', label: 'First name', type: 'text', required: true, autoComplete: 'given-name' },
  { key: 'last', label: 'Last name', type: 'text', required: false, autoComplete: 'family-name' },
  { key: 'email', label: 'Email', type: 'email', required: true, autoComplete: 'email',
    inputMode: 'email' },
  { key: 'mobile', label: 'Mobile', type: 'tel', required: false, autoComplete: 'tel',
    inputMode: 'tel' },
];

/** The complaint against one field, or '' when it has nothing to answer for. */
function fieldError(key, values) {
  const check = detailsCheck(values);
  if (check.over.includes(key)) return `Too long — ${FIELD_LIMITS[key]} characters at most.`;
  if (key === 'first' && !check.named) return 'Needed for the certificate.';
  if (key === 'email') {
    if (!check.hasEmail) return 'Needed — the certificate is sent here.';
    if (!check.emailValid) return 'That does not look like an email address.';
  }
  if (key === 'mobile' && !check.mobileValid) return 'That does not look like a mobile number.';
  return '';
}

/** What the status capsule says, and which tone it takes. */
function pillOf(view) {
  if (!view.open) return { tone: 'shut', text: view.windowLabel };
  if (view.inCount && !view.pending) return { tone: 'in', text: 'Checked in' };
  if (view.phase === 'late' || view.phase === 'ended') return { tone: 'late', text: view.windowLabel };
  return { tone: 'open', text: view.windowLabel };
}

/** The line under the heading. Says what to do, or why there is nothing to do. */
function helperOf(view) {
  if (!view.open) return 'Your pass stays valid — check-in is not open right now.';
  if (view.pending && view.seats.length > 1) {
    return 'Check in each person separately — the name and email you confirm here are '
      + 'the ones their certificate is made from.';
  }
  if (view.pending) return 'Confirm who is using this seat, then check in. You can check out when you leave.';
  if (view.inCount) return 'Checked in. Check out when you leave.';
  return '';
}

/**
 * One seat's details form.
 *
 * The asterisk moves: it marks a group that is still unanswered rather than a
 * fixed pair of fields, because either name and either contact will do. The
 * save stays disabled until both groups are satisfied, and the line above it
 * says which one is still missing — a disabled button with no explanation is
 * the thing people press twice and then leave.
 */
function DetailsForm({ seat, values, onChange, onSubmit, onCancel, busy, idPrefix }) {
  // Nothing is marked wrong before it has been looked at: a form that opens
  // shouting at somebody who has typed nothing yet reads as broken. A field
  // complains once it has been visited, and everything complains once they
  // have tried to submit.
  const [touched, setTouched] = useState({});
  const [tried, setTried] = useState(false);
  const check = detailsCheck(values);
  /* UNIQUE PER COPY, not just per seat. This section is rendered twice on the
     page — once for the rail, once for the phone — and the stylesheet shows
     one. Both are in the DOM, so an id built from the seat alone would exist
     twice: a label would then point at the hidden copy's field, and every
     `document.getElementById` would find the wrong one. */
  const idFor = (key) => `${idPrefix}-${seat.passId}-${key}`;

  const showFor = (key) => (touched[key] || tried ? fieldError(key, values) : '');

  const submit = () => {
    if (!detailsCheck(values).ok) { setTried(true); return; }
    onSubmit();
  };

  return (
    <div className="evb-ci__form">
      <div className="evb-ci__fields">
        {FIELDS.map((field) => {
          const error = showFor(field.key);
          return (
            <div className="evb-ci__f" key={field.key}>
              <label htmlFor={idFor(field.key)}>
                {field.label}
                {field.required && <span className="evb-ci__req"> *</span>}
              </label>
              <input
                id={idFor(field.key)}
                type={field.type}
                autoComplete={field.autoComplete}
                inputMode={field.inputMode}
                // The browser's own stop, so a value too long for the column
                // cannot be typed in the first place. The rule in
                // selfCheckInRules still checks it: a paste, an autofill or a
                // device that ignores the attribute all get past this.
                maxLength={FIELD_LIMITS[field.key]}
                value={values[field.key] || ''}
                onChange={(e) => onChange(field.key, e.target.value)}
                onBlur={() => setTouched((all) => ({ ...all, [field.key]: true }))}
                disabled={busy}
                aria-invalid={error ? 'true' : undefined}
                aria-describedby={error ? `${idFor(field.key)}-err` : undefined}
              />
              {error && (
                <span className="evb-ci__fielderr" id={`${idFor(field.key)}-err`}>{error}</span>
              )}
            </div>
          );
        })}
      </div>

      {/* Silent when there is nothing wrong. A line that congratulates
          somebody for filling in a form is noise in front of a person who is
          standing at a gate. */}
      {!check.ok && (
        <p className="evb-ci__rule" data-ok="no" role="status">
          Needed: {detailsMissing(values)}.
        </p>
      )}

      <div className="evb-ci__formact">
        {/* NOT disabled while incomplete. A disabled button explains nothing to
            somebody who cannot see why; this one accepts the press and then
            says what is missing, with the fields marked. */}
        <button type="button" className="evb-ci__btn" disabled={busy} onClick={submit}>
          {busy ? 'Checking in…' : 'Confirm'}
        </button>
        <button type="button" className="evb-ci__btn evb-ci__btn--ghost" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>
    </div>
  );
}

/**
 * The seats as a queue: how far through, a chip each, one open at a time.
 *
 * WHY NOT A LIST. Ten seats is ten rows — more than a 316px rail or a phone
 * can hold — and ten open forms would be worse. It is also the wrong shape for
 * what happens: people arrive one after another, so the screen should be about
 * whoever is in front of you, with the rest a glance away. The height stops
 * growing once the chips wrap, whether the booking is for three or thirty.
 *
 * Drawn only for a group. One seat has no queue to show, so it keeps the plain
 * card it always had.
 */
function Queue({ seats, activeId, onPick }) {
  const done = seats.filter((seat) => seat.status !== 'ISSUED').length;

  return (
    <div className="evb-ci__q">
      <div className="evb-ci__qhead">
        <span className="evb-ci__qcount">{done} of {seats.length} checked in</span>
        <span className="evb-ci__qbar">
          <span
            className="evb-ci__qfill"
            style={{ width: `${Math.round((done / seats.length) * 100)}%` }}
          />
        </span>
      </div>

      <div className="evb-ci__chips">
        {seats.map((seat, index) => (
          <button
            key={seat.passId}
            type="button"
            className="evb-ci__chip"
            // The state is an attribute rather than a class so the stylesheet
            // and the tests read the same single source.
            data-state={seat.status === 'CHECKED_IN' ? 'in'
              : seat.status === 'CHECKED_OUT' ? 'out' : 'todo'}
            data-active={seat.passId === activeId ? 'yes' : undefined}
            aria-current={seat.passId === activeId ? 'true' : undefined}
            aria-label={`Seat ${index + 1} · ${seat.name || seat.passcode}`}
            onClick={() => onPick(seat)}
          >
            {index + 1}
          </button>
        ))}
      </div>
    </div>
  );
}

export function CheckInPanel({
  event, eventId, passes, now, busy = false, error = null, onCheckIn, onCheckOut,
  // Inside the dialog the heading and the card are the dialog's own, so the
  // panel drops both rather than drawing a card inside a card.
  embedded = false,
  // Told the pass id of the seat on screen, so the dialog can name its activity.
  onActiveSeat,
  // A lone seat opens its form straight away too — the dialog was opened to
  // check in, so a second "Check in" press inside it would only be a step.
  autoOpen = false,
}) {
  // Which seat the queue is on. null means "whoever is next", which is what it
  // should be until somebody says otherwise.
  const [activeId, setActiveId] = useState(null);
  // Which seat has its form open, and what has been typed into it. Keyed by
  // pass id so opening a second seat's form cannot inherit the first's answers.
  // `undefined` means nobody has opened or closed anything yet, which is not
  // the same as `null` (explicitly closed) — in a queue the first state opens
  // the active seat and the second leaves it shut.
  const [openSeat, setOpenSeat] = useState(undefined);
  const [drafts, setDrafts] = useState({});
  // A seat whose double-tap warning the person has overridden.
  const [insisted, setInsisted] = useState({});
  // React's own per-instance id, so the two copies of this section cannot
  // give their fields the same ids.
  const idPrefix = useId();

  const view = selfCheckInView({
    passes,
    event,
    eventId: eventId ?? event?.id,
    now: now || new Date(),
  });

  // A group gets the queue; one seat keeps the card it always had.
  const queue = view.seats.length > 1;
  const nextUp = view.seats.filter((seat) => seat.action === SEAT_ACTION.CHECK_IN);
  const active = queue
    ? (view.seats.find((seat) => seat.passId === activeId) || nextUp[0] || view.seats[0])
    : view.seats[0];
  const activePassId = active ? active.passId : null;

  // Before the early return: a hook must run on every render.
  useEffect(() => {
    if (onActiveSeat) onActiveSeat(activePassId);
  }, [activePassId, onActiveSeat]);

  if (!view.visible) return null;

  const pill = pillOf(view);
  const helper = helperOf(view);
  // The queue shows one seat at a time; a lone seat shows itself.
  const shownSeats = queue && active ? [active] : view.seats;
  /** Whoever the queue moves to once `seat` is done — not `seat` itself. */
  const afterThis = (seat) => nextUp.find((other) => other.passId !== seat.passId) || null;

  const draftFor = (seat) => drafts[seat.passId] || seat.details;

  const setDraft = (seat, key, value) => setDrafts((all) => ({
    ...all,
    [seat.passId]: { ...(all[seat.passId] || seat.details), [key]: value },
  }));

  const submitCheckIn = (seat, nextSeat) => {
    const details = draftFor(seat);
    if (!detailsCheck(details).ok) return;
    // Straight on to whoever is next: there is a queue behind this person, and
    // sending the booker back to find the next seat themselves is the slow way
    // through ten of them. Done locally rather than waiting for the re-read, so
    // the next form is there the moment the last one is sent.
    setActiveId(nextSeat ? nextSeat.passId : seat.passId);
    setOpenSeat(nextSeat ? nextSeat.passId : null);
    onCheckIn({
      seat,
      details,
      // What the pass's user_info should BECOME: everything it already held,
      // with the confirmed fields written over it. The write path sets a json
      // column to exactly what it is given, so sending only the confirmed ones
      // would delete the rest — the gender and age a registration captured.
      userInfo: mergedUserInfo(seat.userInfo, details),
      // Which seat. The server verifies this pass is the sender's before it
      // writes anything; see the action endpoint's owner_checked rule.
      passId: seat.passId,
    });
  };

  return (
    <section
      className={embedded ? 'evb-ci' : 'evb-sec evb-ci'}
      aria-labelledby={embedded ? undefined : `${idPrefix}-h`}
    >
      {!embedded && <h2 className="evb-h" id={`${idPrefix}-h`}>Check-in</h2>}

      {!embedded && (
        <div className="evb-ci__status">
          <span className="evb-ci__pill" data-s={pill.tone}>{pill.text}</span>
        </div>
      )}

      {helper && <p className="evb-note">{helper}</p>}
      {error && <p className="evb-ci__err" role="alert">{error}</p>}

      {queue && (
        <Queue
          seats={view.seats}
          activeId={active ? active.passId : null}
          onPick={(seat) => { setActiveId(seat.passId); setOpenSeat(undefined); }}
        />
      )}

      <div className="evb-ci__seats">
        {shownSeats.map((seat) => {
          // In a queue the seat on screen opens ready to be filled — that is
          // what "next" means. Once somebody cancels, it stays shut until they
          // ask again.
          const formOpen = (queue || autoOpen) && openSeat === undefined
            ? seat.passId === (active && active.passId)
            : openSeat === seat.passId;
          const fresh = seat.action === SEAT_ACTION.RESCAN && !insisted[seat.passId];
          const offers = view.open && seat.action !== SEAT_ACTION.NONE;

          return (
            <div className="evb-ci__seat" key={seat.passId}>
              <div className="evb-ci__row">
                <div className="evb-ci__who">
                  <div className="evb-ci__n">{seat.name || 'Seat not named yet'}</div>
                  <div className="evb-ci__m">
                    {[seat.passcode, seat.checkedInAt && `in ${seat.checkedInAt}`]
                      .filter(Boolean).join(' · ')}
                  </div>
                </div>

                <div className="evb-ci__act">
                  {!offers && (
                    <span className="evb-ci__pill" data-s={seat.status === 'CHECKED_IN' ? 'in' : 'shut'}>
                      {seat.status === 'CHECKED_IN' ? 'Checked in'
                        : seat.status === 'CHECKED_OUT' ? 'Checked out' : 'Not arrived'}
                    </span>
                  )}

                  {/* Gone while the form is open: the form carries its own
                      Cancel, and two controls with the same word in one row is
                      a question rather than an answer. */}
                  {offers && seat.action === SEAT_ACTION.CHECK_IN && !formOpen && (
                    <button
                      type="button"
                      className="evb-ci__btn"
                      disabled={busy}
                      onClick={() => { setActiveId(seat.passId); setOpenSeat(seat.passId); }}
                    >
                      Check in
                    </button>
                  )}

                  {offers && (seat.action === SEAT_ACTION.CHECK_OUT || seat.action === SEAT_ACTION.RESCAN) && (
                    <button
                      type="button"
                      className="evb-ci__btn evb-ci__btn--ghost"
                      disabled={busy}
                      onClick={() => {
                        if (fresh) { setInsisted((all) => ({ ...all, [seat.passId]: true })); return; }
                        onCheckOut({ seat, passId: seat.passId });
                      }}
                    >
                      {fresh ? 'Check out anyway' : busy ? 'Checking out…' : 'Check out'}
                    </button>
                  )}
                </div>
              </div>

              {seat.note && <p className="evb-ci__rule">{seat.note}</p>}

              {formOpen && offers && seat.action === SEAT_ACTION.CHECK_IN && (
                <DetailsForm
                  idPrefix={idPrefix}
                  seat={seat}
                  values={draftFor(seat)}
                  busy={busy}
                  onChange={(key, value) => setDraft(seat, key, value)}
                  onSubmit={() => submitCheckIn(seat, afterThis(seat))}
                  onCancel={() => setOpenSeat(null)}
                />
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

/** "First Last" from a confirmed details form, or ''. */
function nameFrom(details) {
  if (!details) return '';
  return [details.first, details.last].map((part) => String(part || '').trim())
    .filter(Boolean).join(' ');
}

/**
 * The confirmation after a check-in or check-out the server accepted — the
 * web's side of the app's success screen. It REPLACES the dialog's content
 * rather than floating over it: the dialog is short once the form has closed,
 * and a box laid over it was cut off at its edges. Says who, for which activity, and
 * what happens next: the next seat in a group, or that everyone is in.
 */
const DoneNotice = React.forwardRef(function DoneNotice(
  { done, view, eventTitle, activity, onClose, onFinish, titleId }, ref,
) {
  const seats = view.seats || [];
  const index = seats.findIndex((seat) => seat.passId === done.passId);
  // Never the seat just done: until the re-read lands, its pass can still
  // look un-checked, and "Next up" must not point back at it.
  const next = seats.findIndex(
    (seat) => seat.action === SEAT_ACTION.CHECK_IN && seat.passId !== done.passId,
  );
  const out = done.kind === 'out';

  const who = [
    seats.length > 1 && index >= 0 ? `Seat ${index + 1}` : '',
    done.name || '',
  ].filter(Boolean).join(' · ');

  const sub = out ? 'Thanks for coming — see you at the next one!'
    : next >= 0 ? `Next up: Seat ${next + 1}`
      : seats.length > 1 ? 'Everyone on this booking is checked in.'
        : 'Have a great event!';

  return (
    <div
      className="evb-cidone"
      data-kind={done.kind}
      role="alertdialog"
      aria-labelledby={titleId}
      tabIndex={-1}
      ref={ref}
    >
      <span className="evb-cidone__badge" data-kind={done.kind} aria-hidden="true">
        <svg viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="currentColor"
          strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
          {out ? <path d="M15 12H4m4-4-4 4 4 4M13 5h5a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2h-5" />
            : <path d="M5 12.5l4.5 4.5L19 7.5" />}
        </svg>
      </span>
      <h3 className="evb-cidone__t" id={titleId}>
        {out ? 'You’re checked out' : 'You’re checked in'}
      </h3>
      {who && <p className="evb-cidone__who">{who}</p>}
      {/* Two lines at most; the full name and event are in the tooltip. */}
      <p className="evb-cidone__for" title={activity ? `${activity} · ${eventTitle}` : eventTitle}>
        {activity || eventTitle}
      </p>
      <p className="evb-cidone__sub">{sub}</p>
      {/* "Continue" when another seat is waiting in this dialog; otherwise
          the errand is finished, and "Done" closes the dialog too. */}
      <button
        type="button"
        className="evb-ci__btn evb-cidone__go"
        onClick={!out && next >= 0 ? onClose : (onFinish || onClose)}
      >
        {!out && next >= 0 ? 'Continue' : 'Done'}
      </button>
    </div>
  );
});

/**
 * SelfCheckIn — the check-in dialog, opened from a pass card in My
 * Registrations.
 *
 * CONTROLLED: the caller says when it is open and is told when it should
 * close. The card is the invitation — it knows whether this pass can be
 * checked in or out right now — so the dialog has no banner of its own.
 *
 * Portalled to <body> so a sticky site header can never sit over the card and
 * hide its Close button, and so it can be opened from a page whose cards live
 * inside a page-builder frame.
 */
export default function SelfCheckIn(props) {
  const {
    open, onClose, event, eventId, passes, now, activityNames, onCheckIn, onCheckOut,
  } = props;
  // The seat the panel has on screen, so the header can name its activity.
  const [activePassId, setActivePassId] = useState(null);
  // A write the server confirmed: { kind: 'in' | 'out', passId, name }. Drawn
  // as a small modal over the dialog until it is dismissed.
  const [done, setDone] = useState(null);
  const dialogRef = useRef(null);
  const doneRef = useRef(null);
  const headingId = useId();
  const doneTitleId = useId();
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  const view = selfCheckInView({
    passes,
    event,
    eventId: eventId ?? event?.id,
    now: now || new Date(),
  });

  // Escape dismisses the confirmation first, then the dialog; the page behind
  // does not scroll while it is open — a dialog you can scroll out from under
  // is a dialog people lose.
  useEffect(() => {
    if (!open) return undefined;
    const opener = document.activeElement;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      setDone((current) => {
        if (!current && closeRef.current) closeRef.current();
        return null;
      });
    };
    document.addEventListener('keydown', onKey);
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    if (dialogRef.current) dialogRef.current.focus();
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previous;
      setDone(null);
      // Back where they were, so a keyboard does not restart at the top.
      if (opener && typeof opener.focus === 'function') {
        try { opener.focus({ preventScroll: true }); } catch (e) { /* gone */ }
      }
    };
  }, [open]);

  // The confirmation takes focus, so a keyboard or a screen reader lands on it.
  useEffect(() => {
    if (done && doneRef.current) doneRef.current.focus();
  }, [done]);

  if (!open) return null;

  const close = () => { if (onClose) onClose(); };
  const eventTitle = event?.title || 'This event';
  const activityOf = (passId) => {
    const name = passId != null && activityNames ? activityNames[String(passId)] : '';
    return typeof name === 'string' ? name.trim() : '';
  };
  const activity = activityOf(activePassId ?? (view.seats[0] && view.seats[0].passId));

  // The page resolves true once the server has confirmed the write; only then
  // is there anything to congratulate anybody on.
  const runCheckIn = async (args) => {
    const ok = await onCheckIn(args);
    if (ok) setDone({ kind: 'in', passId: args.passId, name: nameFrom(args.details) || args.seat?.name });
    return ok;
  };
  const runCheckOut = async (args) => {
    const ok = await onCheckOut(args);
    if (ok) setDone({ kind: 'out', passId: args.passId, name: args.seat?.name });
    return ok;
  };

  return createPortal((
    <div className="evb evb-portal">
      <div
        className="evb-cimodal"
        onMouseDown={(e) => { if (e.target === e.currentTarget && !done) close(); }}
      >
        <div
          className="evb-cimodal__card"
          role="dialog"
          aria-modal="true"
          aria-labelledby={done ? doneTitleId : headingId}
          tabIndex={-1}
          ref={dialogRef}
        >
          {/* Kept mounted while the confirmation shows, so "Continue" comes
              back to the queue exactly as it was. */}
          <div className="evb-cimodal__main" hidden={Boolean(done)}>
          <div className="evb-cimodal__head">
            <div className="evb-cimodal__titles">
              <p className="evb-cimodal__kicker">Check in</p>
              {/* Two lines at most, then an ellipsis; the full name is in the tooltip. */}
              <h2 className="evb-cimodal__event" id={headingId} title={eventTitle}>{eventTitle}</h2>
              {activity && (
                <p className="evb-cimodal__activity" title={activity}>{activity}</p>
              )}
            </div>
            <button
              type="button"
              className="evb-ci__btn evb-ci__btn--ghost"
              onClick={close}
            >
              Close
            </button>
          </div>
          <div className="evb-cimodal__body">
            {view.visible ? (
              <CheckInPanel
                {...props}
                embedded
                autoOpen
                onActiveSeat={setActivePassId}
                onCheckIn={runCheckIn}
                onCheckOut={runCheckOut}
              />
            ) : (
              // The window closed while the card was on screen.
              <p className="evb-note">Self check-in is not open for this pass right now.</p>
            )}
          </div>

          </div>

          {done && (
            <DoneNotice
              ref={doneRef}
              titleId={doneTitleId}
              done={done}
              view={view}
              eventTitle={eventTitle}
              activity={activityOf(done.passId)}
              onClose={() => setDone(null)}
              onFinish={close}
            />
          )}
        </div>
      </div>
    </div>
  ), document.body);
}
